import { useQuery } from '@tanstack/react-query'

import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { VipTierUpEvent } from '../../types/vip-center'
import { api } from '../../utils/api-client'
import { queryKeys } from './keys'

// Raw vip_check_popup payload: { status, info: { success, has_popup, data } }.
// has_popup sits on info; the popup fields are flat on info.data.
interface RawPopupData {
  ledger_id?: number | string
  from_level_name?: string
  to_level_name?: string
  bonus_amount?: number
  // 'unclaimed' → modal (bomb pop-up) | 'claimed' → toast only
  claim_status?: string
}

interface RawCheckPopupInfo {
  has_popup?: boolean
  data?: RawPopupData
}

// Exported as a pure test seam: maps the raw vip_check_popup info into the
// tier-up event (or null when there's no popup to show).
export const mapPopup = (info: RawCheckPopupInfo): VipTierUpEvent | null => {
  if (!info.has_popup || !info.data) return null

  const p = info.data
  return {
    // ledger_id uniquely identifies the upgrade record.
    id: String(p.ledger_id ?? `${p.to_level_name ?? ''}-${p.claim_status ?? ''}`),
    fromTier: p.from_level_name ?? '',
    toTier: p.to_level_name ?? '',
    amount: p.bonus_amount ?? 0,
    // unclaimed → modal; anything else (claimed) → toast only
    status: p.claim_status === 'unclaimed' ? 'unclaimed' : 'claimed',
  }
}

// FE-04: fetches the not-yet-seen upgrade popup (vip_check_popup) and maps it.
// Exported as a test seam.
export const fetchVipCheckPopup = async (
  signal?: AbortSignal,
): Promise<VipTierUpEvent | null> => {
  const res = await api.post<ApiResponse<RawCheckPopupInfo>>(
    '/vip.php',
    { type: 'vip_check_popup' },
    { signal },
  )
  if (res.data.status !== 1) return null
  return mapPopup(res.data.info ?? {})
}

// FE-04: checks for a not-yet-seen upgrade popup (vip_check_popup). Returns the
// mapped tier-up event, or null when there's nothing to show.
// TODO: this should run app-wide on login/app open; it currently fires when the
// VIP center page mounts (via useVipCenter).
// `enabled` lets a consumer that only needs the aggregated VIP data (e.g. the
// sidebar card) opt out of activating this query, so vip_check_popup isn't fired
// from incidental mounts — only where the popup is actually shown.
const useVipCheckPopup = (enabled = true) => {
  const username = useBoundStore(state => state.user.username)

  return useQuery<VipTierUpEvent | null>({
    queryKey: queryKeys.vipCenter.checkPopup(username),
    queryFn: ({ signal }) => fetchVipCheckPopup(signal),
    enabled: enabled && !!username,
    staleTime: 0,
  })
}

export default useVipCheckPopup
